import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, Tooltip, Button } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { i18n } from '../../translate/i18n';

/** Limite inicial de cards no DOM por coluna (evita travar com milhares de tickets). */
const KANBAN_INITIAL_VISIBLE_CARDS = 100;
const KANBAN_LOAD_MORE_STEP = 100;

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: props.color || '#ebecf0',
    borderRadius: 8,
    minWidth: 272,
    maxWidth: 272,
    height: '100%',
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    minHeight: 0,
  }),
  /** Nome da lane: linha própria no topo para não competir com badge/total (evita "Agendamen..."). */
  laneTitle: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
    color: "#D6D6D6",
    width: '100%',
    lineHeight: 1.35,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    ...theme.scrollbarStyles,
  },
  loadMoreWrap: {
    padding: theme.spacing(0.5, 0),
    flexShrink: 0,
  },
  loadMoreHint: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    display: 'block',
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.35,
  },
  laneHeaderRoot: {
    marginBottom: theme.spacing(1),
  },
  /** Linha inferior: contagem + arrastar | total */
  laneToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    minWidth: 0,
  },
  laneToolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flexShrink: 0,
  },
  totalValue: {
    fontSize: '0.8125rem',
    color: "#D6D6D6",
    fontWeight: 'bold',
    flexShrink: 0,
    textAlign: 'right',
    lineHeight: 1.2,
  },
  dragHandle: {
    cursor: 'grab',
    color: 'rgba(255, 255, 255, 0.7)',
    padding: theme.spacing(0.5),
    '&:hover': {
      color: 'rgba(255, 255, 255, 1)',
    },
  },
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  countBadge: {
    minWidth: 24,
    height: 24,
    padding: '0 7px',
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    // Fundo sempre claro: no dark mode text.primary é claro e sumia no balão
    color: theme.palette.getContrastText('#ffffff'),
    fontSize: '0.75rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
    lineHeight: 1,
  },
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, onTicketClosed, onTicketTransferred, isAdmin }) => {
  const classes = useStyles({ color });

  const [visibleCount, setVisibleCount] = useState(KANBAN_INITIAL_VISIBLE_CARDS);

  useEffect(() => {
    setVisibleCount(KANBAN_INITIAL_VISIBLE_CARDS);
  }, [id]);

  /**
   * Quando tickets.length era 0 (carregando), o clamp antigo fazia visibleCount = 0.
   * Ao passar a haver tickets, Math.min(0, n) permanecia 0 — sumiam todos os cards.
   * Só ajustamos quando já há tickets; garantimos floor INITIAL e teto tickets.length.
   */
  useEffect(() => {
    if (tickets.length === 0) return;
    setVisibleCount(prev =>
      Math.min(Math.max(prev, KANBAN_INITIAL_VISIBLE_CARDS), tickets.length)
    );
  }, [tickets.length]);

  const visibleTickets = useMemo(
    () => tickets.slice(0, visibleCount),
    [tickets, visibleCount]
  );

  const handleLoadMore = useCallback(() => {
    setVisibleCount(c => c + KANBAN_LOAD_MORE_STEP);
  }, []);

  const totalValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + opportunityValue;
  }, 0);

  const ticketCount = tickets.length;

  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.laneHeaderRoot}>
            <Typography className={classes.laneTitle} component="div" variant="subtitle2">
              {title}
            </Typography>
            <div className={classes.laneToolbar}>
              <div className={classes.laneToolbarLeft}>
                <Tooltip
                  title={i18n.t('kanban.laneTicketCountTooltip', { count: ticketCount })}
                >
                  <span
                    className={classes.countBadge}
                    aria-label={i18n.t('kanban.laneTicketCountAria', { count: ticketCount })}
                  >
                    {ticketCount}
                  </span>
                </Tooltip>
                {isAdmin && (
                  <div
                    {...provided.dragHandleProps}
                    className={classes.dragHandle}
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </div>
                )}
              </div>
              <Typography className={classes.totalValue} component="span">
                Total: R$ {totalValue.toFixed(2)}
              </Typography>
            </div>
          </div>
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {visibleTickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                    onTicketClosed={onTicketClosed}
                    onTicketTransferred={onTicketTransferred}
                  />
                ))}
                {ticketCount > visibleTickets.length && (
                  <div className={classes.loadMoreWrap}>
                    <Typography className={classes.loadMoreHint} component="span">
                      {i18n.t('kanban.showingCardsPartial', {
                        visible: visibleTickets.length,
                        total: ticketCount,
                      })}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={handleLoadMore}
                    >
                      {i18n.t('kanban.loadMoreCards')}
                    </Button>
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

const areKanbanColumnPropsEqual = (prevProps, nextProps) => {
  if (
    prevProps.id !== nextProps.id ||
    prevProps.title !== nextProps.title ||
    prevProps.color !== nextProps.color ||
    prevProps.index !== nextProps.index ||
    prevProps.isAdmin !== nextProps.isAdmin ||
    prevProps.updateTicket !== nextProps.updateTicket ||
    prevProps.onTicketClosed !== nextProps.onTicketClosed ||
    prevProps.onTicketTransferred !== nextProps.onTicketTransferred
  ) {
    return false;
  }

  const prevTickets = prevProps.tickets || [];
  const nextTickets = nextProps.tickets || [];

  if (prevTickets.length !== nextTickets.length) return false;

  for (let i = 0; i < prevTickets.length; i += 1) {
    if (prevTickets[i] !== nextTickets[i]) {
      return false;
    }
  }

  return true;
};

export default memo(KanbanColumn, areKanbanColumnPropsEqual);
